/** Tiny Web Audio synth and procedural music: no audio files, everything generated. */
const MUTE_KEY = 'typing-racer.muted';
const MUSIC_KEY = 'typing-racer.music';
const MUSIC_ON_KEY = 'typing-racer.music-on';
const TYPING_KEY = 'typing-racer.typing-sound';
const ENGINE_KEY = 'typing-racer.engine';

export type TypingSound = 'off' | 'soft' | 'clicky' | 'typewriter';
export const TYPING_SOUNDS: { id: TypingSound; name: string; blurb: string }[] = [
  { id: 'off', name: 'Off', blurb: 'Silent keys' },
  { id: 'soft', name: 'Soft', blurb: 'Quiet thock' },
  { id: 'clicky', name: 'Clicky', blurb: 'Mechanical click' },
  { id: 'typewriter', name: 'Typewriter', blurb: 'Clack, with a bell at the end of each line' },
];

let shared: AudioContext | null = null;
/** Lazily created audio context; must first be touched from a user gesture. */
export function audioContext(): AudioContext | null {
  if (!shared) {
    try {
      shared = new AudioContext();
    } catch {
      return null;
    }
  }
  if (shared.state === 'suspended') void shared.resume();
  return shared;
}

export class Synth {
  private engine: OscillatorNode | null = null;
  private engineGain: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  /** Game sound effects (engine, errors, fanfares). */
  muted = false;
  /** Key-press sounds, independent of the effects toggle. */
  typingSound: TypingSound = 'clicky';
  /** Engine hum during races and lessons. Off by default; many people find it tiring. */
  engineOn = false;

  constructor() {
    try {
      this.muted = localStorage.getItem(MUTE_KEY) === '1';
      const t = localStorage.getItem(TYPING_KEY) as TypingSound | null;
      if (t && TYPING_SOUNDS.some((x) => x.id === t)) this.typingSound = t;
      this.engineOn = localStorage.getItem(ENGINE_KEY) === '1';
    } catch {
      /* ignore */
    }
  }

  setEngineOn(on: boolean): void {
    this.engineOn = on;
    try {
      localStorage.setItem(ENGINE_KEY, on ? '1' : '0');
    } catch {
      /* ignore */
    }
    if (!on) this.stopEngine();
  }

  setTypingSound(t: TypingSound): void {
    this.typingSound = t;
    try {
      localStorage.setItem(TYPING_KEY, t);
    } catch {
      /* ignore */
    }
    this.keyPress(); // preview
  }

  setMuted(m: boolean): void {
    this.muted = m;
    try {
      localStorage.setItem(MUTE_KEY, m ? '1' : '0');
    } catch {
      /* ignore */
    }
    const ctx = audioContext();
    if (this.engineGain && ctx) this.engineGain.gain.setTargetAtTime(m ? 0 : 0.05, ctx.currentTime, 0.05);
  }

  private get audio(): AudioContext | null {
    return this.muted ? null : audioContext();
  }

  private blip(freq: number, dur: number, type: OscillatorType, vol: number, slide = 0): void {
    const ctx = this.audio;
    if (!ctx) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, ctx.currentTime);
    if (slide) o.frequency.exponentialRampToValueAtTime(Math.max(20, freq + slide), ctx.currentTime + dur);
    g.gain.setValueAtTime(vol, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + dur);
  }

  /** Short noise burst through a band-pass filter: the body of every key sound. */
  private thock(ctx: AudioContext, freq: number, dur: number, vol: number, q = 1): void {
    if (!this.noiseBuf) {
      this.noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 0.1, ctx.sampleRate);
      const d = this.noiseBuf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    }
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = freq;
    f.Q.value = q;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    src.connect(f).connect(g).connect(ctx.destination);
    src.start();
    src.stop(ctx.currentTime + dur);
  }

  /** Key-press sound in the chosen style. `lineEnd` rings the typewriter bell. */
  keyPress(lineEnd = false): void {
    if (this.typingSound === 'off') return;
    const ctx = audioContext();
    if (!ctx) return;
    const v = 0.9 + Math.random() * 0.2; // slight variation so it doesn't sound robotic
    switch (this.typingSound) {
      case 'soft':
        this.thock(ctx, 900 + Math.random() * 200, 0.05, 0.25 * v, 0.8);
        break;
      case 'clicky':
        this.thock(ctx, 2500 + Math.random() * 600, 0.025, 0.35 * v, 2);
        this.blipRaw(ctx, 3200 + Math.random() * 500, 0.02, 'square', 0.03 * v);
        break;
      case 'typewriter':
        this.thock(ctx, 1400 + Math.random() * 300, 0.06, 0.5 * v, 1.2);
        this.blipRaw(ctx, 5200 + Math.random() * 800, 0.03, 'triangle', 0.06 * v);
        if (lineEnd) setTimeout(() => this.blipRaw(ctx, 2637, 0.6, 'sine', 0.12), 60);
        break;
    }
  }

  private blipRaw(ctx: AudioContext, freq: number, dur: number, type: OscillatorType, vol: number): void {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.value = freq;
    g.gain.setValueAtTime(vol, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + dur);
  }
  error(): void {
    this.blip(140, 0.18, 'sawtooth', 0.12, -60);
  }
  nitro(): void {
    this.blip(300, 0.45, 'sawtooth', 0.12, 900);
  }
  countdown(final: boolean): void {
    this.blip(final ? 880 : 440, final ? 0.5 : 0.15, 'square', 0.1);
  }
  finish(): void {
    [523, 659, 784, 1047].forEach((f, i) => setTimeout(() => this.blip(f, 0.35, 'triangle', 0.15), i * 120));
  }
  star(): void {
    this.blip(1320, 0.25, 'triangle', 0.12, 400);
  }

  startEngine(): void {
    if (!this.engineOn) return;
    const ctx = this.audio;
    if (!ctx || this.engine) return;
    this.engine = ctx.createOscillator();
    this.engine.type = 'sawtooth';
    this.engine.frequency.value = 60;
    this.engineGain = ctx.createGain();
    this.engineGain.gain.value = 0;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 400;
    this.engine.connect(filter).connect(this.engineGain).connect(ctx.destination);
    this.engine.start();
    this.engineGain.gain.setTargetAtTime(0.05, ctx.currentTime, 0.3);
  }

  /** speed01: 0 idle .. 1 flat out */
  setEngine(speed01: number): void {
    const ctx = shared;
    if (!this.engine || !ctx) return;
    this.engine.frequency.setTargetAtTime(55 + speed01 * 160, ctx.currentTime, 0.1);
  }

  stopEngine(): void {
    const ctx = shared;
    if (!this.engine || !ctx || !this.engineGain) return;
    this.engineGain.gain.setTargetAtTime(0, ctx.currentTime, 0.2);
    const e = this.engine;
    setTimeout(() => e.stop(), 800);
    this.engine = null;
    this.engineGain = null;
  }
}

// ------------------------------------------------------------------ music
export type MusicTrack = 'cruise' | 'turbo' | 'focus';
export const MUSIC_TRACKS: { id: MusicTrack; name: string; blurb: string }[] = [
  { id: 'cruise', name: 'Cruise', blurb: 'Laid-back chiptune' },
  { id: 'turbo', name: 'Turbo', blurb: 'Fast and punchy' },
  { id: 'focus', name: 'Focus', blurb: 'Soft pads for lessons' },
];

interface TrackDef {
  bpm: number;
  /** MIDI note numbers per chord, one chord per bar. */
  chords: number[][];
  bass: OscillatorType;
  lead: OscillatorType;
  /** 16-step patterns. */
  bassSteps: number[];
  drums: boolean;
  arpRate: 1 | 2; // steps per lead note
  volume: number;
}

const TRACKS: Record<MusicTrack, TrackDef> = {
  cruise: {
    bpm: 104,
    chords: [
      [60, 64, 67, 71],
      [57, 60, 64, 67],
      [65, 69, 72, 76],
      [67, 71, 74, 77],
    ],
    bass: 'triangle',
    lead: 'square',
    bassSteps: [0, 3, 6, 8, 11, 14],
    drums: true,
    arpRate: 2,
    volume: 0.09,
  },
  turbo: {
    bpm: 150,
    chords: [
      [57, 60, 64, 67],
      [53, 57, 60, 64],
      [60, 64, 67, 71],
      [55, 59, 62, 65],
    ],
    bass: 'sawtooth',
    lead: 'square',
    bassSteps: [0, 2, 4, 6, 8, 10, 12, 14],
    drums: true,
    arpRate: 1,
    volume: 0.08,
  },
  focus: {
    bpm: 72,
    chords: [
      [62, 65, 69, 72],
      [58, 62, 65, 69],
      [60, 64, 67, 71],
      [57, 60, 64, 67],
    ],
    bass: 'sine',
    lead: 'triangle',
    bassSteps: [0, 8],
    drums: false,
    arpRate: 2,
    volume: 0.07,
  },
};

const midi = (n: number) => 440 * 2 ** ((n - 69) / 12);

/**
 * Procedural chiptune player. A lookahead scheduler queues notes slightly ahead of the
 * audio clock so timing stays tight even when the tab is throttled.
 */
export class Music {
  track: MusicTrack = 'cruise';
  enabled = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private step = 0;
  private nextTime = 0;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private def: TrackDef | null = null;
  private running = false;
  private wantPlaying = false;

  constructor() {
    try {
      const t = localStorage.getItem(MUSIC_KEY) as MusicTrack | null;
      if (t && MUSIC_TRACKS.some((m) => m.id === t)) this.track = t;
      this.enabled = localStorage.getItem(MUSIC_ON_KEY) === '1';
    } catch {
      /* ignore */
    }
  }

  setTrack(t: MusicTrack): void {
    this.track = t;
    try {
      localStorage.setItem(MUSIC_KEY, t);
    } catch {
      /* ignore */
    }
    this.stop();
    if (this.enabled && this.wantPlaying) this.start();
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    try {
      localStorage.setItem(MUSIC_ON_KEY, on ? '1' : '0');
    } catch {
      /* ignore */
    }
    if (on) {
      if (this.wantPlaying) this.start();
    } else this.stop();
  }

  /** Call once audio is allowed (after a gesture); music plays if enabled. */
  play(): void {
    this.wantPlaying = true;
    if (this.enabled) this.start();
  }

  /** Call when gameplay ends. */
  pause(): void {
    this.wantPlaying = false;
    this.stop();
  }

  get playing(): boolean {
    return this.running;
  }

  private start(): void {
    if (this.running || !this.enabled) return;
    const ctx = audioContext();
    if (!ctx) return;
    this.def = TRACKS[this.track];
    this.master = ctx.createGain();
    this.master.gain.value = 0;
    this.master.connect(ctx.destination);
    this.master.gain.setTargetAtTime(this.def.volume, ctx.currentTime, 0.5);
    if (!this.noise) {
      this.noise = ctx.createBuffer(1, ctx.sampleRate * 0.2, ctx.sampleRate);
      const d = this.noise.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    }
    this.step = 0;
    this.nextTime = ctx.currentTime + 0.05;
    this.running = true;
    this.timer = setInterval(() => this.schedule(ctx), 25);
  }

  private stop(): void {
    if (!this.running) return;
    this.running = false;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    const ctx = shared;
    const m = this.master;
    if (ctx && m) {
      m.gain.setTargetAtTime(0, ctx.currentTime, 0.15);
      setTimeout(() => m.disconnect(), 600);
    }
    this.master = null;
  }

  private schedule(ctx: AudioContext): void {
    const def = this.def!;
    const stepDur = 60 / def.bpm / 4;
    while (this.nextTime < ctx.currentTime + 0.12) {
      this.playStep(ctx, this.step, this.nextTime, def, stepDur);
      this.nextTime += stepDur;
      this.step = (this.step + 1) % (16 * def.chords.length);
    }
  }

  private tone(ctx: AudioContext, freq: number, t: number, dur: number, type: OscillatorType, vol: number): void {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.value = freq;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.master!);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  private hit(ctx: AudioContext, t: number, dur: number, vol: number, hp: number): void {
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const f = ctx.createBiquadFilter();
    f.type = 'highpass';
    f.frequency.value = hp;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f).connect(g).connect(this.master!);
    src.start(t);
    src.stop(t + dur);
  }

  private playStep(ctx: AudioContext, step: number, t: number, def: TrackDef, stepDur: number): void {
    const bar = Math.floor(step / 16);
    const s = step % 16;
    const chord = def.chords[bar]!;
    // Bass
    if (def.bassSteps.includes(s)) this.tone(ctx, midi(chord[0]! - 24), t, stepDur * 1.8, def.bass, 0.9);
    // Lead arpeggio
    if (s % def.arpRate === 0) {
      const i = (s / def.arpRate) % (chord.length * 2);
      const idx = i < chord.length ? i : chord.length * 2 - 1 - i;
      const octave = bar % 2 === 1 && s >= 8 ? 12 : 0;
      this.tone(ctx, midi(chord[idx]! + octave), t, stepDur * def.arpRate * 0.9, def.lead, 0.35);
    }
    // Pad root every bar for the focus track
    if (!def.drums && s === 0) {
      for (const n of chord) this.tone(ctx, midi(n), t, stepDur * 16, 'sine', 0.25);
    }
    // Drums
    if (def.drums) {
      if (s % 4 === 0) {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.setValueAtTime(150, t);
        o.frequency.exponentialRampToValueAtTime(40, t + 0.12);
        g.gain.setValueAtTime(1, t);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);
        o.connect(g).connect(this.master!);
        o.start(t);
        o.stop(t + 0.16);
      }
      if (s === 4 || s === 12) this.hit(ctx, t, 0.12, 0.5, 1500);
      if (s % 2 === 0) this.hit(ctx, t, 0.04, 0.18, 7000);
    }
  }
}
